import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SetlistCard from '../components/SetlistCard';
import BandCard from '../components/BandCard';
import SheetCard from '../components/SheetCard';
import EventCard from '../components/EventCard';
import { UserIcon, UsersIcon, FileTextIcon, FolderIcon, EditIcon, SaveIcon, XIcon, PlusIcon, TrashIcon, MapPinIcon, LinkIcon, SettingsIcon, LogOutIcon, CalendarIcon, Clock, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, fetchWithAuth } from '../services/ApiService';
import CommentsSection from '../components/Comments/CommentsSection';
import eventBus from '../utils/EventBus';
import { getSystemFormatDescription, formatDateTime, formatTime } from '../utils/dateFormatter';

const ProfilePage = () => {
  const { userId: urlUserId } = useParams();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [userBands, setUserBands] = useState([]);
  const [userSheets, setUserSheets] = useState([]);
  const [leadSheets, setLeadSheets] = useState([]);
  const [userSetlists, setUserSetlists] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityDisplayCount, setActivityDisplayCount] = useState(5);
  const [loading, setLoading] = useState(true);
  // Removed activeTab state - using sections instead
  const [error, setError] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [editUrls, setEditUrls] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  
  // Settings state
  const [includeChordProgressions, setIncludeChordProgressions] = useState(true);
  const [includeSectionColors, setIncludeSectionColors] = useState(true);
  const [minifyContent, setMinifyContent] = useState(false);
  const [pageBreakBeforeChords, setPageBreakBeforeChords] = useState(true); // New date format preference

  // Determine which user's profile to show
  const targetUserId = urlUserId || currentUser?._id;
  const isOwnProfile = !urlUserId || (urlUserId === currentUser?._id);

  // Redirect /profile to /profile/:userId for consistent URLs
  useEffect(() => {
    if (!urlUserId && currentUser?._id) {
      navigate(`/profile/${currentUser._id}`, { replace: true });
    }
  }, [currentUser?._id, urlUserId, navigate]);
  
  // Load settings from localStorage on mount (only for own profile)
  useEffect(() => {
    if (isOwnProfile) {
      const savedChordProgressions = localStorage.getItem('includeChordProgressions');
      const savedSectionColors = localStorage.getItem('includeSectionColors');
      const savedMinifyContent = localStorage.getItem('minifyContent');
      const savedPageBreakBeforeChords = localStorage.getItem('pageBreakBeforeChords');
      
      if (savedChordProgressions !== null) {
        setIncludeChordProgressions(savedChordProgressions === 'true');
      }
      
      if (savedSectionColors !== null) {
        setIncludeSectionColors(savedSectionColors === 'true');
      }
      
      if (savedMinifyContent !== null) {
        setMinifyContent(savedMinifyContent === 'true');
      }
      
      if (savedPageBreakBeforeChords !== null) {
        setPageBreakBeforeChords(savedPageBreakBeforeChords === 'true');
      }
    }
  }, [isOwnProfile]);
  
  // Listen for edit profile event from the local menu
  useEffect(() => {
    const handleEditProfile = () => {
      if (isOwnProfile) {
        setIsEditingProfile(true);
        setEditFirstName(profileUser?.firstName || '');
        setEditLastName(profileUser?.lastName || '');
        setEditCity(profileUser?.city || '');
        setEditCountry(profileUser?.country || '');
        setEditUrls(profileUser?.urls || []);
      }
    };
    
    const editListener = eventBus.on('profile:edit', handleEditProfile);
    
    return () => {
      editListener();
    };
  }, [isOwnProfile, profileUser]);

  // Initial data fetch
  useEffect(() => {
    if (!targetUserId) return;
    
    // For own profile, wait for currentUser to load
    if (!urlUserId && !currentUser) return;
    
    // For other profiles, we can fetch immediately
    if (urlUserId && urlUserId !== currentUser?._id) {
      // Clear previous data to prevent flickering
      setUserSheets([]);
      setUserSetlists([]);
      setUserBands([]);
      setUserEvents([]);
      setLeadSheets([]);
      
      fetchUserData();
    }
  }, [targetUserId, urlUserId]);
  
  // Fetch data when currentUser loads for own profile
  useEffect(() => {
    if (isOwnProfile && currentUser && targetUserId) {
      // Clear previous data to prevent flickering
      setUserSheets([]);
      setUserSetlists([]);
      setUserBands([]);
      setUserEvents([]);
      setLeadSheets([]);
      
      fetchUserData();
    }
  }, [isOwnProfile, currentUser?._id, targetUserId])

  const fetchUserData = async () => {
    // Prevent concurrent fetches
    if (isFetching) return;
    
    setIsFetching(true);
    setLoading(true);
    setError(null);
    try {
      // Recalculate isOwnProfile inside the function to get the current value
      const isCurrentUserProfile = urlUserId === currentUser?._id || (!urlUserId && !!currentUser);
      
      // Fetch profile user info if viewing another user's profile
      if (!isCurrentUserProfile) {
        const userResponse = await fetchWithAuth(`${API_URL}/users/${targetUserId}/profile`);
        if (userResponse && userResponse.data) {
          setProfileUser(userResponse.data);
        } else {
          setError('User not found');
          return;
        }
      } else {
        // Fetch own profile data to get the latest fields
        const userResponse = await fetchWithAuth(`${API_URL}/users/${targetUserId}/profile`);
        if (userResponse && userResponse.data) {
          setProfileUser(userResponse.data);
        } else {
          setProfileUser(currentUser);
        }
      }

      // Use unified endpoint - backend handles authorization
      const userDataResponse = await fetchWithAuth(`${API_URL}/users/${targetUserId}`);
      if (userDataResponse && userDataResponse.data) {
        setUserBands(userDataResponse.data.bands || []);
        setUserSheets(userDataResponse.data.sheets || []);
        setUserSetlists(userDataResponse.data.setlists || []);
        
        // Filter sheets where user is mentioned as lead
        const sheets = userDataResponse.data.sheets || [];
        const username = profileUser?.username || currentUser?.username;
        const leadSheetsFiltered = sheets.filter(sheet => {
          return (sheet.leadVocalist && username && sheet.leadVocalist.includes(`@${username}`)) ||
                 (sheet.arranger && username && sheet.arranger.includes(`@${username}`))
        });
        setLeadSheets(leadSheetsFiltered);
      }
      
      // Fetch recent activity (fetch 20 but show 10 initially)
      const activityResponse = await fetchWithAuth(`${API_URL}/users/${targetUserId}/activity?limit=20`);
      if (activityResponse && activityResponse.data) {
        setRecentActivity(activityResponse.data);
      }
      
      // Fetch user's events (only for own profile)
      if (isCurrentUserProfile) {

        // Fetch user's events (my events, band events, and RSVP'd events)
        const eventsResponse = await fetchWithAuth(`${API_URL}/events`);
        if (eventsResponse && eventsResponse.data) {
          // Filter events: owned by user, from user's bands, or user has RSVP'd
          const relevantEvents = eventsResponse.data.filter(event => {
            // Events owned by the user
            if (event.owner?._id === targetUserId || event.owner === targetUserId) {
              return true;
            }
            // Events from user's bands
            const userBands = userDataResponse?.data?.bands || [];
            if (userBands.some(band => band._id === event.band?._id || band._id === event.band)) {
              return true;
            }
            // Events user is attending (invited to)
            if (event.attendees?.some(attendee => 
              attendee.user?._id === targetUserId || attendee.user === targetUserId
            )) {
              return true;
            }
            return false;
          });
          // Sort by start time, upcoming first
          relevantEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
          // Only show upcoming events
          const upcomingEvents = relevantEvents.filter(event => new Date(event.startTime) >= new Date());
          setUserEvents(upcomingEvents.slice(0, 6)); // Limit to 6 events
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  if (!currentUser && isOwnProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const displayUser = profileUser || currentUser;

  // Profile editing functions
  const handleStartEditProfile = () => {
    setIsEditingProfile(true);
    setEditFirstName(displayUser?.firstName || '');
    setEditLastName(displayUser?.lastName || '');
    setEditCity(displayUser?.city || '');
    setEditCountry(displayUser?.country || '');
    setEditUrls([...(displayUser?.urls || [])]);
  };

  const handleSaveProfile = async () => {
    try {
      const profileData = {
        firstName: editFirstName,
        lastName: editLastName,
        city: editCity,
        country: editCountry,
        urls: editUrls
      };

      const response = await fetchWithAuth(`${API_URL}/users/${currentUser._id}/profile`, {
        method: 'PUT',
        body: JSON.stringify(profileData)
      });

      if (response && response.data) {
        // Update the profile user state
        setProfileUser(prev => ({
          ...prev,
          firstName: editFirstName,
          lastName: editLastName,
          city: editCity,
          country: editCountry,
          urls: editUrls
        }));
        setIsEditingProfile(false);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancelProfile = () => {
    setIsEditingProfile(false);
    setEditFirstName(displayUser?.firstName || '');
    setEditLastName(displayUser?.lastName || '');
    setEditCity(displayUser?.city || '');
    setEditCountry(displayUser?.country || '');
    setEditUrls([...(displayUser?.urls || [])]);
  };

  const handleAddUrl = () => {
    setEditUrls(prev => [...prev, { label: '', url: '' }]);
  };

  const handleRemoveUrl = (index) => {
    setEditUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateUrl = (index, field, value) => {
    setEditUrls(prev => prev.map((urlObj, i) => 
      i === index ? { ...urlObj, [field]: value } : urlObj
    ));
  };
  
  // Save settings to localStorage
  const handleSaveSettings = async () => {
    localStorage.setItem('includeChordProgressions', includeChordProgressions.toString());
    localStorage.setItem('includeSectionColors', includeSectionColors.toString());
    localStorage.setItem('minifyContent', minifyContent.toString());
    localStorage.setItem('pageBreakBeforeChords', pageBreakBeforeChords.toString());
    
    // If user is logged in, also save to database
    if (currentUser && isOwnProfile) {
      try {
        const response = await fetch(`${API_URL}/users/${currentUser._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            preferences: {
              // dateFormat will be implemented later
            }
          })
        });
        
        if (!response.ok) {
          console.error('Failed to save preferences to server');
        }
      } catch (error) {
        console.error('Error saving preferences:', error);
      }
    }
    
    // Show success message (you could use a toast notification here instead)
    alert('Settings saved successfully!');
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="px-6 py-8">
            {isEditingProfile ? (
              // Edit mode for profile
              <div>
                <div className="flex items-start space-x-6 mb-6">
                  <div className="flex-shrink-0">
                    {displayUser?.avatar ? (
                      <img 
                        src={displayUser.avatar} 
                        alt={displayUser.username}
                        className="w-16 h-16 rounded-full"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">{displayUser?.username}</h1>
                    
                    {/* Name fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter first name..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter last name..."
                        />
                      </div>
                    </div>
                    
                    {/* Location fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MapPinIcon className="w-4 h-4 inline mr-1" />
                          City
                        </label>
                        <input
                          type="text"
                          value={editCity}
                          onChange={(e) => setEditCity(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter city..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          value={editCountry}
                          onChange={(e) => setEditCountry(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter country..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* URLs Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      <LinkIcon className="w-4 h-4 inline mr-1" />
                      Social Links & URLs
                    </label>
                    <button
                      onClick={handleAddUrl}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PlusIcon className="w-3 h-3 mr-1" />
                      Add URL
                    </button>
                  </div>
                  
                  {editUrls.length > 0 ? (
                    <div className="space-y-3">
                      {editUrls.map((urlObj, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={urlObj.label}
                              onChange={(e) => handleUpdateUrl(index, 'label', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Label (e.g., LinkedIn, Facebook)"
                            />
                            <input
                              type="url"
                              value={urlObj.url}
                              onChange={(e) => handleUpdateUrl(index, 'url', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="https://..."
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveUrl(index)}
                            className="p-2 text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">No URLs added yet. Click "Add URL" to add social links.</p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveProfile}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <SaveIcon className="w-4 h-4 mr-2" />
                    Save Profile
                  </button>
                  <button
                    onClick={handleCancelProfile}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <XIcon className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Display mode for profile
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {displayUser?.avatar ? (
                    <img 
                      src={displayUser.avatar} 
                      alt={displayUser.username}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                      <UserIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <h1 className="text-2xl font-bold text-gray-900">@{displayUser?.username}</h1>
                  
                  {/* Display full name if available */}
                  {(displayUser?.firstName || displayUser?.lastName) && (
                    <p className="text-lg text-gray-700 mt-1">
                      {displayUser.firstName} {displayUser.lastName}
                    </p>
                  )}
                  
                  {/* Display location if available */}
                  {(displayUser?.city || displayUser?.country) && (
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <MapPinIcon className="w-4 h-4 mr-1" />
                      {[displayUser.city, displayUser.country].filter(Boolean).join(', ')}
                    </p>
                  )}
                  
                  {/* Display URLs if available */}
                  {displayUser?.urls && displayUser.urls.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {displayUser.urls.map((urlObj, index) => (
                        <a
                          key={index}
                          href={urlObj.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                        >
                          <LinkIcon className="w-3 h-3 mr-1" />
                          {urlObj.label || 'Link'}
                        </a>
                      ))}
                    </div>
                  )}
                  
                  {isOwnProfile && <p className="text-gray-600 mt-2">{displayUser?.email}</p>}
                  <p className="text-sm text-gray-500 mt-2">
                    Member since {new Date(displayUser?.created_at || displayUser?.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {isOwnProfile && (
                  <div className="flex-shrink-0 flex gap-2">
                    <button 
                      onClick={handleStartEditProfile}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
                    >
                      <EditIcon className="w-3 h-3" />
                      Edit Profile
                    </button>
                    <button 
                      onClick={() => {
                        logout();
                        navigate('/');
                      }}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
                    >
                      <LogOutIcon className="w-3 h-3" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <UsersIcon className="w-8 h-8 text-blue-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{userBands.length}</h3>
                <p className="text-gray-600">Bands</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <FileTextIcon className="w-8 h-8 text-green-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{userSheets.length}</h3>
                <p className="text-gray-600">Sheets</p>
              </div>
            </div>
          </div>
          {leadSheets.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <UserIcon className="w-8 h-8 text-blue-500" />
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">{leadSheets.length}</h3>
                  <p className="text-gray-600">Lead Sheets</p>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <FolderIcon className="w-8 h-8 text-purple-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{userSetlists.length}</h3>
                <p className="text-gray-600">Setlists</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <CalendarIcon className="w-8 h-8 text-orange-500" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{userEvents.length}</h3>
                <p className="text-gray-600">Events</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-600">Loading...</div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-center items-center py-12">
              <div className="text-red-600">{error}</div>
            </div>
          </div>
        ) : (
          <>
            {/* Recent Activity Section */}
            <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Recent Activity
              </h2>
              <div className="space-y-3">
                {recentActivity.length > 0 ? (
                  <>
                    {recentActivity.slice(0, activityDisplayCount).map((item, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex-1">
                          {item.itemType === 'sheet' ? (
                            <Link 
                              to={`/sheets/${item.id}`}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {item.title}
                            </Link>
                          ) : (
                            <Link 
                              to={`/setlists/${item.id}`}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {item.name}
                            </Link>
                          )}
                          <div className="text-sm text-gray-500 mt-1">
                            {item.itemType === 'sheet' && item.artist && (
                              <span className="mr-3">by {item.artist}</span>
                            )}
                            {item.itemType === 'setlist' && item.band_name && (
                              <span className="mr-3">{item.band_name}</span>
                            )}
                            <span className="text-xs">
                              {item.activityType === 'commented' ? 'Commented on' : 'Created'} • 
                              {' '}{new Date(item.activityTime).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 ml-4">
                          {item.itemType === 'sheet' ? 'Sheet' : 'Setlist'}
                        </div>
                      </div>
                    ))}
                    {recentActivity.length > activityDisplayCount && (
                      <button
                        onClick={() => setActivityDisplayCount(prev => Math.min(prev + 5, recentActivity.length))}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Show more ({Math.min(5, recentActivity.length - activityDisplayCount)} more)
                      </button>
                    )}
                    {activityDisplayCount > 5 && (
                      <button
                        onClick={() => setActivityDisplayCount(5)}
                        className="text-gray-500 hover:text-gray-700 text-sm font-medium ml-3"
                      >
                        Show less
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-gray-500">No recent activity</div>
                )}
                {userBands.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-gray-600">
                      Active in: 
                      <Link 
                        to={`/bands/${userBands[0]._id}`}
                        className="font-medium text-blue-600 hover:text-blue-800 ml-1"
                      >
                        {userBands[0].name}
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Bands Section */}
            <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <UsersIcon className="w-5 h-5 mr-2" />
                Bands
              </h2>
              {userBands.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-md">
                  <p className="text-gray-500">No bands yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userBands.map((band) => (
                    <BandCard 
                      key={band._id} 
                      band={band}
                      canManage={false}
                      context="profile"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Sheets Section */}
            <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center">
                  <FileTextIcon className="w-5 h-5 mr-2" />
                  Sheets
                </h2>
                {userSheets.length > 9 && (
                  <Link 
                    to="/sheets" 
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View all {userSheets.length} sheets →
                  </Link>
                )}
              </div>
              {userSheets.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-md">
                  <p className="text-gray-500">No sheets yet</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {userSheets.slice(0, 9).map((sheet) => (
                      <SheetCard 
                        key={sheet._id || sheet.id} 
                        sheet={sheet}
                        canManage={false}
                        context="profile"
                      />
                    ))}
                  </div>
                  {userSheets.length > 9 && (
                    <div className="mt-4 text-center">
                      <Link 
                        to="/sheets" 
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        View all {userSheets.length} sheets
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Lead Sheets Section */}
            {leadSheets.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold flex items-center">
                    <UserIcon className="w-5 h-5 mr-2" />
                    Lead Sheets
                  </h2>
                  <span className="text-sm text-gray-500">
                    {leadSheets.length} sheet{leadSheets.length !== 1 ? 's' : ''} where {isOwnProfile ? 'you are' : `${profileUser?.username || 'they are'}`} mentioned as lead vocalist or arranger
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {leadSheets.map((sheet) => (
                    <SheetCard 
                      key={sheet._id || sheet.id} 
                      sheet={sheet}
                      canManage={false}
                      context="profile"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Setlists Section */}
            <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FolderIcon className="w-5 h-5 mr-2" />
                Setlists
              </h2>
              {userSetlists.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-md">
                  <p className="text-gray-500">No setlists yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userSetlists.map((setlist) => (
                    <SetlistCard 
                      key={setlist._id}
                      setlist={setlist}
                      canManage={false}
                      context="profile"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Events Section */}
            <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center">
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  Upcoming Events
                </h2>
                {userEvents.length > 0 && (
                  <Link 
                    to="/events" 
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View calendar →
                  </Link>
                )}
              </div>
              {userEvents.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-md">
                  <p className="text-gray-500">No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {userEvents.map(event => (
                    <EventCard 
                      key={event._id} 
                      event={event} 
                      onMenuClick={(e) => e.stopPropagation()} // Prevent navigation on menu click
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Settings Section - Only show for own profile */}
            {isOwnProfile && (
              <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <SettingsIcon className="w-5 h-5 mr-2" />
                  Settings
                </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Print View Defaults</h3>
                      <p className="text-sm text-gray-600 mb-6">
                        Configure default settings for how your sheets appear when printed or exported to PDF.
                      </p>
                      
                      <div className="space-y-4">
                        {/* System Date Format Display */}
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">
                            Date & Time Format
                          </label>
                          <div className="p-3 bg-gray-50 rounded-md">
                            <p className="text-sm text-gray-700">
                              Using system format: <span className="font-medium">{getSystemFormatDescription()}</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Dates and times will display according to your computer's regional settings.
                            </p>
                          </div>
                        </div>
                        
                        <hr className="my-4" />
                        
                        {/* Print Settings */}
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Print Settings</h4>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="include-chord-progressions"
                            checked={includeChordProgressions}
                            onChange={(e) => setIncludeChordProgressions(e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="include-chord-progressions" className="ml-3 block text-sm text-gray-900">
                            Include chord progressions on a last page
                          </label>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="include-section-colors"
                            checked={includeSectionColors}
                            onChange={(e) => setIncludeSectionColors(e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="include-section-colors" className="ml-3 block text-sm text-gray-900">
                            Include section background colors
                          </label>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="minify-content"
                            checked={minifyContent}
                            onChange={(e) => setMinifyContent(e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="minify-content" className="ml-3 block text-sm text-gray-900">
                            Minify content to save space
                          </label>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="page-break-before-chords"
                            checked={pageBreakBeforeChords}
                            onChange={(e) => setPageBreakBeforeChords(e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="page-break-before-chords" className="ml-3 block text-sm text-gray-900">
                            Add page break before chord progressions section
                          </label>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <button
                          onClick={handleSaveSettings}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <SaveIcon className="w-4 h-4 mr-2" />
                          Save Settings
                        </button>
                      </div>
                    </div>
                    
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4">Account</h3>
                      <div className="space-y-4">
                        <button
                          onClick={handleLogout}
                          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <LogOutIcon className="w-4 h-4 mr-2" />
                          Log Out
                        </button>
                      </div>
                    </div>
                  </div>
              </div>
            )}
          </>
        )}

        {/* Comments Section - Always visible at bottom */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-8">
          <CommentsSection
            objectType="user"
            objectId={targetUserId}
            allowComments={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;